import { supabaseClient } from '@auth-v2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    createLayout,
    deleteLayout,
    fetchLayoutsByUser,
    fetchLayouts,
    Layout,
    LayoutCreate,
    LayoutUpdate,
    updateLayout,
    upsertLayout,
    LAYOUT_TYPE,
    getLatestUsedLayouts,
    fetchLayoutById,
} from '@data';
import { activeUserIdAtom, currentUserIdAtom, useEnsureAtomValue } from '@store';

import { queryKeys } from '../queryKeys';

export const useLayoutById = (layoutId: number) => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useQuery({
        queryKey: queryKeys.layout.one(activeUserId, layoutId),
        queryFn: () => fetchLayoutById(layoutId),
        enabled: !!activeUserId && layoutId >= 0,
    });
};

export const useLayouts = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useQuery({
        queryKey: queryKeys.layout.all(activeUserId),
        queryFn: () => fetchLayoutsByUser(supabaseClient, activeUserId),
        enabled: !!activeUserId,
    });
};

export const useUserLayouts = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useQuery({
        queryKey: queryKeys.layout.user(activeUserId),
        queryFn: () => fetchLayoutsByUser(supabaseClient, activeUserId, [LAYOUT_TYPE.PRIVATE]),
        enabled: !!activeUserId,
    });
};

export const useUsersLastUsedLayouts = (clubId?: number, limit?: number) => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const lastUsed = { clubId, limit };
    return useQuery({
        queryKey: queryKeys.layout.lastUsed(activeUserId, lastUsed),
        queryFn: () => getLatestUsedLayouts(lastUsed),
        enabled: !!activeUserId,
    });
};

export const usePublicLayouts = () => {
    const currentUserId = useEnsureAtomValue(currentUserIdAtom);
    return useQuery({
        queryKey: queryKeys.layout.public(currentUserId),
        queryFn: () => fetchLayouts([LAYOUT_TYPE.DEFAULT, LAYOUT_TYPE.PUBLIC]),
    });
};

export const useDefaultLayout = () => {
    const currentUserId = useEnsureAtomValue(currentUserIdAtom);
    return useQuery({
        queryKey: queryKeys.layout.default(currentUserId),
        queryFn: async () => {
            const data = await fetchLayouts([LAYOUT_TYPE.DEFAULT]);
            return data?.[0] ?? null;
        },
    });
};

export const useDeleteLayout = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (layoutId: number) => {
            return deleteLayout(supabaseClient, activeUserId, layoutId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.all(activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.user(activeUserId),
            });
        },
    });
};

export const useUpsertLayout = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (layout: Omit<Layout, 'ownerUserId'> | Omit<Layout, 'id' | 'ownerUserId'>) =>
            upsertLayout(supabaseClient, activeUserId, {
                ...layout,
                ownerUserId: activeUserId,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.all(activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.user(activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.default(activeUserId),
            });
        },
    });
};

export const useUpdateLayout = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => {},
        mutationFn: (update: LayoutUpdate) => updateLayout(supabaseClient, update),
        onSuccess: (layout) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.all(activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.user(activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.default(activeUserId),
            });
            if (!layout) return;
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.one(activeUserId, layout.id),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layoutCustomization.key(activeUserId, layout.id),
            });
        },
    });
};

export const useCreateLayout = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    const queryClient = useQueryClient();
    return useMutation({
        onMutate: () => {},
        mutationFn: (update: LayoutCreate) => createLayout(supabaseClient, update),
        onSuccess: (layout) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.all(layout?.ownerUserId ?? activeUserId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layout.user(layout?.ownerUserId ?? activeUserId),
            });
        },
    });
};
